/** @format */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Container, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import { colors } from "../../utility/types/constant";
import { formatCurrency } from "../../utility/currencyUtils";
import HowItWorksModal from "../how-it-works/HowItWorksModal";
import {
    Avatar,
    AvatarPulse,
    AvatarSlot,
    CardHeader,
    CardLabel,
    CenterPot,
    ContribChip,
    CtaRow,
    FinePrint,
    FloatChip,
    FloatChipLabel,
    FloatChipValue,
    GhostButton,
    GlassCard,
    GoldButton,
    GridOverlay,
    HeroBadge,
    HeroSection,
    HeroSubtitle,
    HeroTitle,
    LegendDot,
    LegendItem,
    LegendRow,
    LinkButton,
    LiveDot,
    MemberName,
    MemberRow,
    Orb,
    PotAmount,
    PotLabel,
    RingProgress,
    RingStage,
    RingTrack,
    StatusChip,
    TealSpan,
    TrustItem,
    TrustRow,
    VisualWrap,
} from "./styles";

// Same illustrative circle referenced by the i18n rotation copy below the fold.
const MEMBERS = [
    { name: "Sarah Chen", avatar: "/assets/images/avatars/avatar_1.jpg" },
    { name: "Marcus Johnson", avatar: "/assets/images/avatars/avatar_2.jpg" },
    { name: "Aisha Patel", avatar: "/assets/images/avatars/avatar_3.jpg" },
    { name: "David Kim", avatar: "/assets/images/avatars/avatar_4.jpg" },
    { name: "Elena Rodriguez", avatar: "/assets/images/avatars/avatar_5.jpg" },
    { name: "James Wilson", avatar: "/assets/images/avatars/avatar_6.jpg" },
];

const CONTRIBUTION = 50;
const POOL = CONTRIBUTION * MEMBERS.length;

/** 300ms per tick: ticks 1-6 collect contributions, 9+ pays the pot out. */
const TICK_MS = 300;
const TICKS_PER_ROUND = 18;
const PAYOUT_TICK = 9;

type MemberState = "current" | "received" | "awaiting";

const CircleHero: React.FC = () => {
    const { t } = useTranslation("home");
    const navigate = useNavigate();
    const heroRef = useRef<HTMLElement>(null);
    const [howItWorksOpen, setHowItWorksOpen] = useState(false);
    const [tick, setTick] = useState(0);
    const [animated, setAnimated] = useState(true);

    // Slide the hero up behind the floating navbar pill. Falls back to no
    // offset when the navbar is absent or the measurement looks wrong.
    useEffect(() => {
        const el = heroRef.current;
        if (!el) return;
        const applyOffset = () => {
            const nav = el.previousElementSibling as HTMLElement | null;
            const height = nav ? nav.getBoundingClientRect().height : 0;
            el.style.setProperty(
                "--ff-nav-offset",
                height > 0 && height < 240 ? `${Math.round(height)}px` : "0px",
            );
        };
        applyOffset();
        window.addEventListener("resize", applyOffset);
        return () => window.removeEventListener("resize", applyOffset);
    }, []);

    useEffect(() => {
        const prefersReduced =
            typeof window.matchMedia === "function" &&
            window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if (prefersReduced) {
            setAnimated(false);
            return;
        }
        const id = setInterval(() => setTick((prev) => prev + 1), TICK_MS);
        return () => clearInterval(id);
    }, []);

    const step = animated ? tick % TICKS_PER_ROUND : PAYOUT_TICK;
    const round = animated ? Math.floor(tick / TICKS_PER_ROUND) : 0;
    const activeIndex = round % MEMBERS.length;
    const isPayout = step >= PAYOUT_TICK;
    const contributed = isPayout ? MEMBERS.length : Math.min(step, MEMBERS.length);
    const potValue = contributed * CONTRIBUTION;

    const stateOf = (index: number): MemberState =>
        index === activeIndex
            ? "current"
            : index < activeIndex
                ? "received"
                : "awaiting";

    const statusLabel: Record<MemberState, string> = {
        current: t("circleVisualization.receiving"),
        received: t("circleVisualization.received"),
        awaiting: t("circleVisualization.awaiting"),
    };
    const statusTone: Record<MemberState, string> = {
        current: colors.secondaryMain,
        received: colors.mainSecondary,
        awaiting: "#9aa6c4",
    };

    // Gradient arc showing how much of the pot has been collected this cycle.
    const ringProgress = useMemo(() => {
        const degrees = (contributed / MEMBERS.length) * 360;
        return `conic-gradient(from -90deg, ${colors.mainSecondary} 0deg, ${colors.mainSecondary} ${degrees}deg, rgba(255,255,255,0.06) ${degrees}deg 360deg)`;
    }, [contributed]);

    // "Save together. Pay out fairly." → accent the closing clause.
    const heading = t("bannerOne.heading");
    const splitAt = heading.lastIndexOf(". ");
    const headingLead = splitAt > -1 ? heading.slice(0, splitAt + 1) : heading;
    const headingAccent = splitAt > -1 ? heading.slice(splitAt + 2) : "";

    const visibleMembers = MEMBERS.slice(0, 3);

    return (
        <HeroSection ref={heroRef}>
            <GridOverlay />
            <Orb
                sx={{
                    width: 420,
                    height: 420,
                    top: "-120px",
                    left: "-110px",
                    background: "rgba(30,193,149,0.24)",
                }}
            />
            <Orb
                sx={{
                    width: 480,
                    height: 480,
                    bottom: "-180px",
                    right: "-140px",
                    background: "rgba(255,176,0,0.16)",
                    animationDelay: "5s",
                }}
            />

            <Container maxWidth="lg" sx={{ position: "relative" }}>
                <Grid container spacing={{ xs: 0, md: 6 }} alignItems="center">
                    <Grid item xs={12} md={7}>
                        <Box
                            sx={{
                                textAlign: { xs: "center", md: "left" },
                                maxWidth: { xs: "620px", md: "none" },
                                mx: { xs: "auto", md: 0 },
                            }}>
                            <HeroBadge>
                                <LiveDot />
                                {t("socialProof.stat1Number")} · {t("socialProof.stat1Label")}
                            </HeroBadge>

                            <HeroTitle>
                                {headingLead}
                                {headingAccent && (
                                    <>
                                        {" "}
                                        <TealSpan>{headingAccent}</TealSpan>
                                    </>
                                )}
                            </HeroTitle>

                            <HeroSubtitle>{t("bannerOne.mainTitle")}</HeroSubtitle>

                            <CtaRow>
                                <GoldButton
                                    variant="contained"
                                    disableElevation
                                    endIcon={<ArrowForwardIcon />}
                                    onClick={() => navigate("/circle/sign-up")}>
                                    {t("bannerOne.cta")}
                                </GoldButton>
                                <GhostButton
                                    variant="text"
                                    startIcon={<PlayCircleOutlineIcon />}
                                    onClick={() => navigate("/sandbox/demo")}>
                                    {t("bannerOne.tryLiveDemo")}
                                </GhostButton>
                                <LinkButton
                                    variant="text"
                                    onClick={() => setHowItWorksOpen(true)}>
                                    {t("bannerOne.ctaSecondary")}
                                </LinkButton>
                            </CtaRow>

                            <TrustRow>
                                <TrustItem>
                                    <LockOutlinedIcon
                                        sx={{ fontSize: 16, color: colors.mainSecondary }}
                                    />
                                    {t("trustQuestions.q1Tag")}
                                </TrustItem>
                                <TrustItem>
                                    <VerifiedUserOutlinedIcon
                                        sx={{ fontSize: 16, color: colors.mainSecondary }}
                                    />
                                    {t("trustQuestions.q3Tag")}
                                </TrustItem>
                                <TrustItem>
                                    <BoltOutlinedIcon
                                        sx={{ fontSize: 16, color: colors.mainSecondary }}
                                    />
                                    {t("supportedCountriesSection.statStripe")}
                                </TrustItem>
                            </TrustRow>

                            <FinePrint>{t("whySafer.disclaimer")}</FinePrint>
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={5}>
                        <VisualWrap>
                            <FloatChip sx={{ top: 0, right: "6%" }}>
                                <SavingsOutlinedIcon
                                    sx={{ fontSize: 20, color: colors.mainSecondary }}
                                />
                                <span>
                                    <FloatChipLabel>{t("bannerTwo.automation")}</FloatChipLabel>
                                    <FloatChipValue>
                                        +{formatCurrency(CONTRIBUTION)}
                                    </FloatChipValue>
                                </span>
                            </FloatChip>

                            <FloatChip sx={{ bottom: 0, left: "6%", animationDelay: "2.5s" }}>
                                <PaymentsOutlinedIcon
                                    sx={{ fontSize: 20, color: colors.secondaryMain }}
                                />
                                <span>
                                    <FloatChipLabel>
                                        {t("circleVisualization.receiving")}
                                    </FloatChipLabel>
                                    <FloatChipValue>{formatCurrency(POOL)}</FloatChipValue>
                                </span>
                            </FloatChip>

                            <GlassCard>
                                <CardHeader>
                                    <CardLabel>{t("circleVisualization.example")}</CardLabel>
                                    <LiveDot />
                                </CardHeader>

                                <RingStage>
                                    <RingTrack />
                                    <RingProgress style={{ background: ringProgress }} />

                                    <CenterPot>
                                        <PotAmount paying={isPayout}>
                                            {formatCurrency(isPayout ? POOL : potValue)}
                                        </PotAmount>
                                        <PotLabel>
                                            {t("circleVisualization.monthlyPool")}
                                        </PotLabel>
                                    </CenterPot>

                                    {MEMBERS.map((member, index) => {
                                        const angle =
                                            (index / MEMBERS.length) * 2 * Math.PI - Math.PI / 2;
                                        const state = stateOf(index);
                                        return (
                                            <AvatarSlot
                                                key={member.name}
                                                style={{
                                                    left: `${50 + Math.cos(angle) * 38}%`,
                                                    top: `${50 + Math.sin(angle) * 38}%`,
                                                }}>
                                                <Box sx={{ position: "relative" }}>
                                                    <Avatar
                                                        src={member.avatar}
                                                        alt={member.name}

                                                        state={state}
                                                    />
                                                    {state === "current" && isPayout && <AvatarPulse />}
                                                    {!isPayout && index < contributed && (
                                                        <ContribChip>
                                                            +{formatCurrency(CONTRIBUTION)}
                                                        </ContribChip>
                                                    )}
                                                </Box>
                                            </AvatarSlot>
                                        );
                                    })}
                                </RingStage>

                                <LegendRow>
                                    {(["current", "received", "awaiting"] as MemberState[]).map(
                                        (state) => (
                                            <LegendItem key={state}>
                                                <LegendDot tone={statusTone[state]} />
                                                {statusLabel[state]}
                                            </LegendItem>
                                        ),
                                    )}
                                </LegendRow>

                                {visibleMembers.map((member, index) => {
                                    const state = stateOf(index);
                                    return (
                                        <MemberRow key={member.name}>
                                            <Avatar
                                                src={member.avatar}
                                                alt={member.name}
                                                state={state}
                                                style={{ width: 28, flexShrink: 0 }}
                                            />
                                            <MemberName>{member.name}</MemberName>
                                            <StatusChip tone={statusTone[state]}>
                                                {statusLabel[state]}
                                            </StatusChip>
                                        </MemberRow>
                                    );
                                })}
                            </GlassCard>
                        </VisualWrap>
                    </Grid>
                </Grid>
            </Container>

            <HowItWorksModal open={howItWorksOpen} onOpenChange={setHowItWorksOpen} />
        </HeroSection>
    );
};

export default CircleHero;
